import type {
  DeskOverviewCoverState,
  DeskOverviewRag,
  DeskReviewReason,
} from '../api/desk-overview';

// The Desk Overview column set: every prototype column is available —
// an important default subset visible, the rest toggleable.
export type DeskOverviewColumnGroup =
  | 'Order'
  | 'People'
  | 'Purchases'
  | 'Sales'
  | 'Coverage'
  | 'Positions';

export type DeskOverviewColumnKey =
  | 'reference'
  | 'createdAt'
  | 'counterparty'
  | 'metal'
  | 'qp'
  | 'financing'
  | 'stonexAccounts'
  | 'trader'
  | 'operations'
  | 'buyQty'
  | 'buyPriced'
  | 'buyBalance'
  | 'sellQty'
  | 'sellPriced'
  | 'sellBalance'
  | 'exposureMt'
  | 'allocatedMt'
  | 'uncoveredMt'
  | 'hedgeableQtyMt'
  | 'hedgedNetMt'
  | 'rag'
  | 'review'
  | 'positionByAccount'
  | 'coverState';

export interface DeskOverviewColumnDef {
  key: DeskOverviewColumnKey;
  label: string;
  group: DeskOverviewColumnGroup;
  defaultVisible: boolean;
  numeric?: boolean;
  sortable?: boolean;
}

export const DESK_OVERVIEW_COLUMNS: readonly DeskOverviewColumnDef[] = [
  { key: 'reference', label: 'Order', group: 'Order', defaultVisible: true, sortable: true },
  { key: 'createdAt', label: 'Created', group: 'Order', defaultVisible: true, sortable: true },
  { key: 'counterparty', label: 'Counterparty', group: 'Order', defaultVisible: true },
  { key: 'metal', label: 'Metal', group: 'Order', defaultVisible: true },
  { key: 'trader', label: 'Trader', group: 'People', defaultVisible: true },
  { key: 'operations', label: 'Ops', group: 'People', defaultVisible: true },
  { key: 'qp', label: 'QP', group: 'Order', defaultVisible: true },
  { key: 'buyQty', label: 'Pur. qty', group: 'Purchases', defaultVisible: true, numeric: true },
  {
    key: 'buyPriced',
    label: 'Pur. priced',
    group: 'Purchases',
    defaultVisible: true,
    numeric: true,
  },
  {
    key: 'buyBalance',
    label: 'Pur. bal.',
    group: 'Purchases',
    defaultVisible: true,
    numeric: true,
  },
  { key: 'sellQty', label: 'Sales qty', group: 'Sales', defaultVisible: true, numeric: true },
  {
    key: 'sellPriced',
    label: 'Sales priced',
    group: 'Sales',
    defaultVisible: true,
    numeric: true,
  },
  { key: 'sellBalance', label: 'Sales bal.', group: 'Sales', defaultVisible: true, numeric: true },
  {
    key: 'exposureMt',
    label: 'Exposure MT',
    group: 'Coverage',
    defaultVisible: true,
    numeric: true,
  },
  {
    key: 'allocatedMt',
    label: 'Allocated MT',
    group: 'Coverage',
    defaultVisible: true,
    numeric: true,
  },
  {
    key: 'uncoveredMt',
    label: 'Uncovered MT',
    group: 'Coverage',
    defaultVisible: true,
    numeric: true,
  },
  { key: 'coverState', label: 'State', group: 'Coverage', defaultVisible: true },
  {
    key: 'hedgeableQtyMt',
    label: 'Hedgeable MT',
    group: 'Coverage',
    defaultVisible: false,
    numeric: true,
  },
  {
    key: 'hedgedNetMt',
    label: 'Hedged net MT',
    group: 'Coverage',
    defaultVisible: false,
    numeric: true,
  },
  { key: 'review', label: 'Review', group: 'Coverage', defaultVisible: true },
  {
    key: 'positionByAccount',
    label: 'Position by account',
    group: 'Positions',
    defaultVisible: true,
  },
  { key: 'stonexAccounts', label: 'StoneX accounts', group: 'Positions', defaultVisible: false },
  { key: 'financing', label: 'Financing', group: 'Order', defaultVisible: true },
  { key: 'rag', label: 'RAG', group: 'Coverage', defaultVisible: true },
];

export const DESK_OVERVIEW_DEFAULT_VISIBLE_KEYS: ReadonlySet<string> = new Set(
  DESK_OVERVIEW_COLUMNS.filter((column) => column.defaultVisible).map((column) => column.key),
);

// What each state word means — shown on the badge itself and in the legend, so the two can never
// drift apart. Keyed on the union, so a new server state cannot be added without a meaning.
export const DESK_OVERVIEW_STATE_MEANINGS: Record<DeskOverviewCoverState, string> = {
  covered: 'cover matches exposure — within half a lot above it',
  'partially covered': 'the measured BLs are covered, but one BL could not be measured',
  'unevenly covered': 'cover sits on a BL that cannot use it, and cannot be moved to one that can',
  'exposure pending': 'the measured BLs need no cover, but one BL could not be measured',
  short: 'less cover than exposure',
  unhedged: 'exposure with no cover at all',
  'over-hedged': 'more cover than exposure — a position to unwind',
  'no cover required': 'no exposure to hedge',
  'not assessed': 'exposure cannot be measured yet',
};

// Not a cover state — the marker for an order whose exposure could not be summed in full. One
// constant so the badge and the legend cannot describe it differently.
export const DESK_OVERVIEW_PARTIAL_MEANING =
  "a BL's exposure is missing, so this order's totals are incomplete";

// Not a cover state either — the marker for an order whose deal was cancelled.
// One constant so the badge's tooltip and the legend entry cannot describe it
// differently, the way the partial marker's does.
export const DESK_OVERVIEW_CANCELLED_MEANING =
  'the order was cancelled; this row stays while a hedge allocation on it is live';

// Terse, for controls such as the RAG filter; DESK_OVERVIEW_RAG_MEANINGS below is
// the long form and is unusable as a dropdown option.
export const DESK_OVERVIEW_RAG_LABELS: Record<DeskOverviewRag, string> = {
  green: 'Green',
  amber: 'Amber',
  red: 'Red',
  not_assessed: 'Not assessed',
};

// The dot's accessible name and the table legend.
export const DESK_OVERVIEW_RAG_MEANINGS: Record<DeskOverviewRag, string> = {
  green: 'Covered, or no cover required',
  amber: 'Needs a decision',
  red: 'Blocks the cycle: unhedged, short, over-hedged or unevenly covered',
  not_assessed: 'Excluded or not assessed',
};

// Why a BL needs the desk to look at it. Shown on both review badges, so the
// order row and the BL row name the same condition the same way.
export const DESK_OVERVIEW_REVIEW_REASON_MEANINGS: Record<DeskReviewReason, string> = {
  cancelled: 'the BL was deleted while holding live cover',
  financier_mismatch: 'the BL no longer belongs to the lender holding cover on it',
  ineligible: 'the BL is not eligible for financing',
};

// One badge title from a set of reasons, deduplicated and in a stable order so
// two rows carrying the same reasons read identically.
export function describeReviewReasons(reasons: readonly DeskReviewReason[]): string {
  const ordered = REVIEW_REASON_ORDER.filter((reason) => reasons.includes(reason));
  return ordered.map((reason) => DESK_OVERVIEW_REVIEW_REASON_MEANINGS[reason]).join('; ');
}

const REVIEW_REASON_ORDER: readonly DeskReviewReason[] = [
  'cancelled',
  'financier_mismatch',
  'ineligible',
];

export type DeskOverviewSortKey = 'createdAt' | 'reference';
export type DeskOverviewSortDir = 'asc' | 'desc';

export interface DeskOverviewSort {
  key: DeskOverviewSortKey;
  dir: DeskOverviewSortDir;
}

export const DESK_OVERVIEW_DEFAULT_SORT: DeskOverviewSort = { key: 'createdAt', dir: 'desc' };

export function isSortKey(value: string): value is DeskOverviewSortKey {
  return value === 'createdAt' || value === 'reference';
}
